import { Button } from '@components/core-ui/form-elements/buttons/Button';
import { TagDangerRounded } from '@components/core-ui/Tag/variations/TagDangerRounded';
import { cn } from '@helpers/cn';
import { useAPI } from '@providers/useAPI';
import { useQuery } from '@tanstack/react-query';
import { GetBillingAccountResponseDto } from 'app/API';
import { billingKeys } from 'app/queries/billing/billingKeys';
import { createFetchAsOnePlansQueryFn } from 'app/queries/billing/createFetchAsOnePlansQueryFn';
import { createFetchPublishedAsOnePlansQueryFn } from 'app/queries/billing/createFetchPublishedAsOnePlansQueryFn';

import { AddSubscription } from './AddSubscription';
import { ErrorCard } from './Cards/ErrorCard';
import { SkeletonCard } from './Cards/SkeletonCard';
import { SubscriptionCard } from './SubscriptionCard';

import { Wrapper } from './styles/Wrapper';

type TProps = {
  billingAccount?: GetBillingAccountResponseDto;
  isAsOneAllowed: boolean;
  isBillingAccountLoading: boolean;
  reloadPage: () => void;
  hasPaymentMethod?: boolean;
};

export const SubscriptionsWrapper = ({
  billingAccount,
  isAsOneAllowed,
  isBillingAccountLoading,
  reloadPage,
}: TProps) => {
  const { call } = useAPI();

  const allPlansQuery = useQuery({
    queryKey: billingKeys.allAsOnePlans(),
    queryFn: createFetchAsOnePlansQueryFn(call),
    enabled: isAsOneAllowed,
  });
  const publishedPlansQuery = useQuery({
    queryKey: billingKeys.publishedAsOnePlans(),
    queryFn: createFetchPublishedAsOnePlansQueryFn(call),
    enabled: isAsOneAllowed,
  });

  if (!isAsOneAllowed) {
    return null;
  }

  const retryFailedPlans = () => {
    if (allPlansQuery.isError) {
      void allPlansQuery.refetch();
    }
    if (publishedPlansQuery.isError) {
      void publishedPlansQuery.refetch();
    }
  };
  const isRetrying = allPlansQuery.isFetching || publishedPlansQuery.isFetching;

  if (allPlansQuery.isLoadingError || publishedPlansQuery.isLoadingError) {
    return (
      <Wrapper>
        <ErrorCard onRetry={retryFailedPlans} />
      </Wrapper>
    );
  }

  if (isBillingAccountLoading || allPlansQuery.isLoading || publishedPlansQuery.isLoading) {
    const subscriptions = billingAccount?.subscriptions ?? [];

    return (
      <Wrapper aria-busy="true">
        <span className="sr-only">Loading subscriptions</span>
        {subscriptions.length ? subscriptions.map((sub) => <SkeletonCard key={sub.id} />) : <SkeletonCard />}
      </Wrapper>
    );
  }

  const allPlans = allPlansQuery.data;
  const publishedPlans = publishedPlansQuery.data;

  if (!billingAccount || !allPlans || !publishedPlans) {
    return null;
  }

  const hasRefreshError = allPlansQuery.isRefetchError || publishedPlansQuery.isRefetchError;

  return (
    <>
      {hasRefreshError ? (
        <div className="flex items-center justify-end gap-3 pt-5" role="status">
          <TagDangerRounded text="Failed to refresh" />
          <Button disabled={isRetrying} onClick={retryFailedPlans}>
            Retry
          </Button>
        </div>
      ) : null}
      <Wrapper className={cn(hasRefreshError && '!pt-3')}>
        {(billingAccount.subscriptions ?? []).map((sub) => {
          const plan = allPlans.find((plan) => plan.id === sub.productId);
          if (!plan) {
            return null;
          }
          return (
            <SubscriptionCard
              key={sub.id}
              subscription={sub}
              plan={plan}
              billingAccount={billingAccount}
              onChange={reloadPage}
              availablePlans={publishedPlans}
            />
          );
        })}
        <AddSubscription billingAccount={billingAccount} onSuccess={reloadPage} />
      </Wrapper>
    </>
  );
};
